import { Injectable } from '@angular/core';

export type UserRole = 'MOVAL_IE_USER' | 'MOVAL_USER' | 'DSP';

export type PermissionKey =
  | 'addClaim.registerWithRc'
  | 'addClaim.autoSendSurveyLink'
  | 'ila.uploadDoc'
  | 'finalReport.uploadDoc'
  | 'assessment.aiExtraction'
  | 'assessment.excelImport'
  | 'assessment.anyFileTypeUpload'
  | 'claimTat.sendReminder'
  | 'tatMaster.sms'
  | 'tatMaster.email'
  | 'tatMaster.whatsapp'
  | 'letterReminder.autoMail'
  | 'letterReminder.autoSms'
  | 'letterReminder.autoWhatsapp';

// Central role access matrix. Anything not listed for a role is denied.
// Restricted options stay visible in the UI but are rendered disabled.
const ROLE_PERMISSIONS: Record<UserRole, PermissionKey[]> = {
  MOVAL_IE_USER: [
    'assessment.excelImport',
  ],
  MOVAL_USER: [
    'addClaim.registerWithRc',
    'addClaim.autoSendSurveyLink',
    'ila.uploadDoc',
    'finalReport.uploadDoc',
    'assessment.aiExtraction',
    'assessment.excelImport',
    'assessment.anyFileTypeUpload',
    'claimTat.sendReminder',
    'tatMaster.sms',
    'tatMaster.email',
    'tatMaster.whatsapp',
    'letterReminder.autoMail',
    'letterReminder.autoSms',
    'letterReminder.autoWhatsapp',
  ],
  DSP: [],
};
ROLE_PERMISSIONS.DSP = [...ROLE_PERMISSIONS.MOVAL_USER];

export const USER_TYPE_STORAGE_KEY = 'user_type';
export const EXCEL_ACCEPT = '.xls,.xlsx';
export const EXCEL_ONLY_MESSAGE = 'Only Excel files are allowed.';

const EXCEL_EXTENSIONS = ['xls', 'xlsx'];
const EXCEL_MIME_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

@Injectable({ providedIn: 'root' })
export class PermissionService {

  static normalizeRole(userType: string | null | undefined): UserRole {
    const value = (userType || '').toString().trim().toUpperCase().replace(/[\s-]+/g, '_');
    if (value === 'MOVAL_IE_USER') return 'MOVAL_IE_USER';
    if (value === 'DSP') return 'DSP';
    return 'MOVAL_USER';
  }

  get role(): UserRole {
    return PermissionService.normalizeRole(localStorage.getItem(USER_TYPE_STORAGE_KEY));
  }

  get isMovalIEUser(): boolean {
    return this.role === 'MOVAL_IE_USER';
  }

  can(permission: PermissionKey): boolean {
    return ROLE_PERMISSIONS[this.role].includes(permission);
  }

  /** File picker `accept` value for Estimate / Supplementary / Proforma / Invoice uploads. */
  assessmentUploadAccept(defaultAccept = ''): string {
    return this.can('assessment.anyFileTypeUpload') ? defaultAccept : EXCEL_ACCEPT;
  }

  isExcelFile(file: File | null | undefined): boolean {
    if (!file) return false;
    const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
    if (!EXCEL_EXTENSIONS.includes(ext)) return false;
    // Some browsers/OSes report an empty MIME type for Excel files; accept that when the extension matches.
    return !file.type || EXCEL_MIME_TYPES.includes(file.type);
  }

  /** Returns true when the selected file is allowed for the current role's assessment uploads. */
  isAllowedAssessmentUpload(file: File | null | undefined): boolean {
    return this.can('assessment.anyFileTypeUpload') || this.isExcelFile(file);
  }
}
